import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import {
  ActivityIndicator,
  Pressable,
  ScrollView,
  StyleSheet,
  Text,
  View,
} from "react-native";
import { CheckBox } from "@rneui/themed";

import {
  Input,
  NormalText,
  PrimaryButton,
  SecondaryButton,
  TitleText,
} from "../../../common/native";
import { SessionContext } from "../../../../contexts";
import {
  TClinicApiKey,
  TCreateClinicApiKeyResponse,
  createClinicApiKey,
  deleteClinicApiKey,
  listClinicApiKeys,
} from "../../../../services/api-key-management";

type TFormState = {
  name: string;
  allowedScopes: string[];
  expiresAt: string;
};

type TScopeOption = {
  value: string;
  description: string;
  required?: boolean;
};

const SCOPE_OPTIONS: TScopeOption[] = [
  {
    value: "billing:manage",
    description:
      "Full billing management (plan changes, subscription updates, and checkout sessions).",
    required: true,
  },
  {
    value: "billing:read",
    description: "Read-only access to invoices and payment history.",
  },
];

const DEFAULT_FORM_STATE: TFormState = {
  name: "",
  allowedScopes: ["billing:manage"],
  expiresAt: "",
};

export function getBillingUrl(apiKey: string, clinicUUID: string) {
  const origin = window.location.origin;
  const params = new URLSearchParams({
    api_key: apiKey,
    clinic_uuid: clinicUUID,
  });
  return `${origin}/billing?${params.toString()}`;
}

const insertSoftBreaks = (value: string) =>
  value.replace(/(.{4})/g, "$1\u200B");

const formatDateTime = (value: string | null) => {
  if (!value) return "Never";
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value;
  return parsed.toLocaleString();
};

const KeyManagementPage: React.FC = () => {
  const session = useContext(SessionContext)?.session;

  const [isLoading, setIsLoading] = useState(true);
  const [keys, setKeys] = useState<TClinicApiKey[]>([]);
  const [listError, setListError] = useState<string | null>(null);

  const [formState, setFormState] = useState(DEFAULT_FORM_STATE);
  const [formError, setFormError] = useState<string | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const [createdKey, setCreatedKey] =
    useState<TCreateClinicApiKeyResponse | null>(null);

  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [deleteInFlight, setDeleteInFlight] = useState<number | null>(null);

  const [clipboardMessage, setClipboardMessage] = useState<string | null>(null);
  const clipboardTimerRef = useRef<number | null>(null);

  /** Load keys */
  useEffect(() => {
    if (!session?.clinicId) {
      setIsLoading(false);
      return;
    }

    let mounted = true;

    const load = async () => {
      setIsLoading(true);
      setListError(null);
      try {
        const data = await listClinicApiKeys(session);
        if (mounted) setKeys(data);
      } catch (e: any) {
        if (mounted) {
          setListError(e?.message || "Failed to load API keys.");
        }
      } finally {
        if (mounted) setIsLoading(false);
      }
    };

    load();
    return () => {
      mounted = false;
    };
  }, [session]);

  useEffect(() => {
    return () => {
      if (clipboardTimerRef.current) {
        window.clearTimeout(clipboardTimerRef.current);
      }
    };
  }, []);

  const refreshKeys = async () => {
    try {
      setListError(null);
      const data = await listClinicApiKeys(session!);
      setKeys(data);
    } catch (e: any) {
      setListError(e?.message || "Failed to refresh API keys.");
    }
  };

  const handleCopy = async (value: string, msg: string) => {
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(value);
        setClipboardMessage(msg);
        if (clipboardTimerRef.current) {
          window.clearTimeout(clipboardTimerRef.current);
        }
        clipboardTimerRef.current = window.setTimeout(
          () => setClipboardMessage(null),
          4000,
        );
      } else {
        setClipboardMessage("Clipboard unavailable — copy manually.");
      }
    } catch {
      setClipboardMessage("Clipboard unavailable — copy manually.");
    }
  };

  const handleToggleScope = (opt: TScopeOption) => {
    if (opt.required) return;

    setFormState((prev) => {
      const exists = prev.allowedScopes.includes(opt.value);
      if (exists) {
        return {
          ...prev,
          allowedScopes: prev.allowedScopes.filter((s) => s !== opt.value),
        };
      }
      return {
        ...prev,
        allowedScopes: [...prev.allowedScopes, opt.value],
      };
    });
  };

  /** CREATE KEY */
  const handleCreateKey = async () => {
    setFormError(null);

    if (!formState.name.trim()) {
      setFormError("A descriptive name is required.");
      return;
    }
    if (formState.allowedScopes.length === 0) {
      setFormError("Select at least one scope.");
      return;
    }

    let expiresAtIso: string | undefined;
    if (formState.expiresAt.trim()) {
      const parsed = new Date(formState.expiresAt.trim());
      if (Number.isNaN(parsed.getTime())) {
        setFormError("Expiration must be a valid ISO date.");
        return;
      }
      expiresAtIso = parsed.toISOString();
    }

    setIsCreating(true);
    try {
      const payload: any = {
        name: formState.name.trim(),
        allowed_scopes: formState.allowedScopes,
      };
      if (expiresAtIso) payload.expires_at = expiresAtIso;

      const key = await createClinicApiKey(session!, payload);
      setCreatedKey(key);
      setFormState(DEFAULT_FORM_STATE);

      await refreshKeys();
    } catch (e: any) {
      setFormError(e?.message || "Unable to create API key.");
    } finally {
      setIsCreating(false);
    }
  };

  /** DELETE KEY */
  const handleDelete = async (apiKey: TClinicApiKey) => {
    if (
      typeof window !== "undefined" &&
      !window.confirm(
        `Revoke ${apiKey.name}? It will stop working immediately.`,
      )
    ) {
      return;
    }

    setDeleteError(null);
    setDeleteInFlight(apiKey.id);
    try {
      await deleteClinicApiKey(session!, apiKey.id);
      await refreshKeys();
    } catch (e: any) {
      setDeleteError(e?.message || "Unable to revoke API key.");
    } finally {
      setDeleteInFlight(null);
    }
  };

  /** SHARE LINK (sessionless, cookie-less) */
  const shareLink =
    createdKey?.key && getBillingUrl(createdKey.key, createdKey.clinic_uuid);

  const displayKey = createdKey?.key ? insertSoftBreaks(createdKey.key) : null;

  const displayShareLink = shareLink ? insertSoftBreaks(shareLink) : null;

  if (!session) {
    return (
      <View style={styles.centeredContainer}>
        <NormalText text="You must be signed in to manage API keys." />
      </View>
    );
  }

  return (
    <ScrollView
      style={styles.container}
      contentContainerStyle={styles.contentContainer}
      showsVerticalScrollIndicator={false}
    >
      <TitleText text="API Key Management" />
      <NormalText
        text="Issue and revoke API keys for clinic integrations. Treat every key like a password—store it securely and rotate regularly."
        style={styles.pageLead}
      />

      {clipboardMessage && (
        <View style={styles.infoBanner}>
          <NormalText text={clipboardMessage} style={styles.infoBannerText} />
        </View>
      )}

      {session.role !== "CLINIC_ADMIN" ? (
        <View style={styles.warningBox}>
          <NormalText
            text="Only clinic administrators can create and revoke API keys. You can still view existing keys."
            style={styles.warningText}
          />
        </View>
      ) : null}

      <View style={styles.section}>
        <TitleText text="Create a new key" style={styles.sectionTitle} />
        <View style={styles.formCard}>
          <Input
            name="Key name"
            placeholder="Pipedrive integration"
            value={formState.name}
            onChangeText={(value) =>
              setFormState((previous) => ({ ...previous, name: value }))
            }
          />
          <Input
            name="Expiration (optional)"
            placeholder="2026-01-01 or 2026-01-01T00:00:00Z"
            value={formState.expiresAt}
            onChangeText={(value) =>
              setFormState((previous) => ({ ...previous, expiresAt: value }))
            }
          />
          <NormalText
            text="Leave blank for a non-expiring key. We recommend setting an expiration whenever possible."
            style={styles.helperText}
          />

          <View style={styles.scopeContainer}>
            <NormalText text="Allowed scopes" style={styles.scopeHeading} />
            <View style={styles.scopeList}>
              {SCOPE_OPTIONS.map((option) => {
                const checked = formState.allowedScopes.includes(option.value);
                return (
                  <View style={styles.scopeRow} key={option.value}>
                    <CheckBox
                      checked={checked}
                      onPress={() => handleToggleScope(option)}
                      disabled={
                        option.required || session.role !== "CLINIC_ADMIN"
                      }
                      containerStyle={styles.checkboxContainer}
                      size={20}
                    />
                    <View style={styles.scopeCopy}>
                      <NormalText
                        text={option.value}
                        style={styles.scopeTitle}
                      />
                      <NormalText
                        text={option.description}
                        style={styles.scopeDescription}
                      />
                      {option.required ? (
                        <NormalText
                          text="Required for billing endpoint access."
                          style={styles.scopeRequired}
                        />
                      ) : null}
                    </View>
                  </View>
                );
              })}
            </View>
          </View>

          {formError ? (
            <NormalText text={formError} style={styles.errorText} />
          ) : null}

          <PrimaryButton
            text="Create API key"
            onPress={handleCreateKey}
            loading={isCreating}
            disabled={
              session.role !== "CLINIC_ADMIN" ||
              isCreating ||
              !formState.name.trim() ||
              formState.allowedScopes.length === 0
            }
          />
        </View>
      </View>

      {createdKey ? (
        <View style={styles.createdKeyCard}>
          <TitleText
            text="New API key"
            style={styles.createdKeyTitle}
            fontWeight="700"
          />
          <NormalText
            text="This is the only time we will show the raw key. Copy it to your secrets manager before leaving this page."
            style={styles.createdKeyBody}
          />
          <Pressable
            style={styles.copyBlock}
            onPress={() =>
              handleCopy(createdKey.key, "API key copied to clipboard")
            }
          >
            <Text style={styles.copyValue}>{displayKey ?? createdKey.key}</Text>
          </Pressable>
          <SecondaryButton
            text="Copy API key"
            onPress={() =>
              handleCopy(createdKey.key, "API key copied to clipboard")
            }
            fullWidth
          />
          {shareLink ? (
            <View style={styles.shareSection}>
              <NormalText
                text="Shareable billing link"
                style={styles.shareLabel}
              />
              <Pressable
                style={styles.copyBlockLight}
                onPress={() =>
                  handleCopy(shareLink, "Billing link copied to clipboard")
                }
              >
                <Text style={styles.shareValue}>
                  {displayShareLink ?? shareLink}
                </Text>
              </Pressable>
              <SecondaryButton
                text="Copy billing link"
                onPress={() =>
                  handleCopy(shareLink, "Billing link copied to clipboard")
                }
                fullWidth
              />
              <NormalText
                text="Warn anyone you share this link with that it grants full billing access."
                style={styles.shareHint}
              />
            </View>
          ) : null}
          <SecondaryButton
            text="Hide key"
            onPress={() => setCreatedKey(null)}
            fullWidth
          />
        </View>
      ) : null}

      <View style={styles.section}>
        <View style={styles.sectionHeader}>
          <TitleText text="Active keys" style={styles.sectionTitle} />
          <SecondaryButton
            text="Refresh"
            onPress={refreshKeys}
            loading={isLoading}
            disabled={isLoading}
          />
        </View>

        {listError ? (
          <View style={styles.errorBanner}>
            <NormalText text={listError} style={styles.errorBannerText} />
          </View>
        ) : null}

        {deleteError ? (
          <View style={styles.errorBanner}>
            <NormalText text={deleteError} style={styles.errorBannerText} />
          </View>
        ) : null}

        {isLoading ? (
          <View style={styles.loader}>
            <ActivityIndicator size="small" color="#00B7F4" />
          </View>
        ) : null}

        {!isLoading && keys.length === 0 ? (
          <View style={styles.emptyState}>
            <NormalText
              text="No active API keys yet."
              style={styles.emptyTitle}
            />
            <NormalText
              text="Create a key above to get started. We recommend generating a dedicated key per integration."
              style={styles.emptyCopy}
            />
          </View>
        ) : null}

        {!isLoading && keys.length > 0 ? (
          <View style={styles.keysList}>
            {keys.map((apiKey) => (
              <View key={apiKey.id} style={styles.keyCard}>
                <View style={styles.keyHeader}>
                  <NormalText
                    text={apiKey.name}
                    style={styles.keyTitle}
                    numberOfLines={2}
                  />
                  <SecondaryButton
                    text="Revoke"
                    onPress={() => handleDelete(apiKey)}
                    loading={deleteInFlight === apiKey.id}
                    disabled={
                      session.role !== "CLINIC_ADMIN" ||
                      deleteInFlight === apiKey.id
                    }
                  />
                </View>
                <NormalText
                  text={`Prefix: ${apiKey.prefix}`}
                  style={styles.keyPrefix}
                  numberOfLines={2}
                />
                <NormalText
                  text={`Clinic UUID: ${apiKey.clinic_uuid}`}
                  style={styles.keyPrefix}
                  numberOfLines={2}
                />
                <View style={styles.metaRow}>
                  <View style={styles.metaItem}>
                    <NormalText text="Created" style={styles.metaLabel} />
                    <NormalText
                      text={formatDateTime(apiKey.created_at)}
                      style={styles.metaValue}
                    />
                  </View>
                  <View style={styles.metaItem}>
                    <NormalText text="Last used" style={styles.metaLabel} />
                    <NormalText
                      text={formatDateTime(apiKey.last_used_at)}
                      style={styles.metaValue}
                    />
                  </View>
                  <View style={styles.metaItem}>
                    <NormalText text="Expires" style={styles.metaLabel} />
                    <NormalText
                      text={formatDateTime(apiKey.expires_at)}
                      style={styles.metaValue}
                    />
                  </View>
                </View>
                <View style={styles.badgeRow}>
                  {apiKey.allowed_scopes.map((scope) => (
                    <View key={scope} style={styles.scopeBadge}>
                      <NormalText text={scope} style={styles.scopeBadgeText} />
                    </View>
                  ))}
                </View>
              </View>
            ))}
          </View>
        ) : null}
      </View>
    </ScrollView>
  );
};

const styles = StyleSheet.create({
  container: {
    width: "100%",
  },
  contentContainer: {
    gap: 24,
    paddingBottom: 80,
  },
  pageLead: {
    marginTop: 8,
    maxWidth: 720,
    color: "#4D5360",
  },
  infoBanner: {
    backgroundColor: "#E6F7FF",
    borderRadius: 12,
    padding: 12,
    borderWidth: 1,
    borderColor: "#B3E5FC",
  },
  infoBannerText: {
    color: "#025B7E",
  },
  warningBox: {
    borderRadius: 12,
    padding: 12,
    borderWidth: 1,
    borderColor: "#FFCA7A",
    backgroundColor: "#FFF7ED",
  },
  warningText: {
    color: "#8B4A00",
  },
  section: {
    gap: 16,
  },
  sectionHeader: {
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 12,
  },
  sectionTitle: {
    marginBottom: 4,
  },
  formCard: {
    backgroundColor: "#FFFFFF",
    borderRadius: 16,
    padding: 20,
    borderWidth: 1,
    borderColor: "#E2E4EA",
    gap: 16,
  },
  helperText: {
    color: "#636776",
    fontSize: 14,
  },
  scopeContainer: {
    gap: 12,
  },
  scopeHeading: {
    fontFamily: "Urbanist600",
    color: "#282B33",
  },
  scopeList: {
    gap: 12,
  },
  scopeRow: {
    flexDirection: "row",
    alignItems: "flex-start",
    gap: 12,
  },
  checkboxContainer: {
    backgroundColor: "transparent",
    borderWidth: 0,
    padding: 0,
    margin: 0,
  },
  scopeCopy: {
    flex: 1,
    gap: 4,
  },
  scopeTitle: {
    fontFamily: "Urbanist600",
    color: "#1F242F",
  },
  scopeDescription: {
    color: "#4D5360",
    fontSize: 14,
  },
  scopeRequired: {
    fontSize: 13,
    color: "#055F36",
  },
  errorText: {
    color: "#B42318",
  },
  createdKeyCard: {
    backgroundColor: "#0B1F33",
    borderRadius: 16,
    padding: 24,
    gap: 16,
  },
  createdKeyTitle: {
    color: "#FFFFFF",
  },
  createdKeyBody: {
    color: "#D1E6FF",
  },
  copyBlock: {
    backgroundColor: "#112C47",
    borderRadius: 12,
    padding: 16,
    borderWidth: 1,
    borderColor: "#1E3A5F",
  },
  copyValue: {
    fontFamily: "Urbanist600",
    color: "#FFFFFF",
  },
  shareSection: {
    gap: 12,
  },
  shareLabel: {
    color: "#D1E6FF",
    fontFamily: "Urbanist600",
  },
  copyBlockLight: {
    backgroundColor: "#F2FAFE",
    borderRadius: 12,
    padding: 16,
    borderWidth: 1,
    borderColor: "#C4EEFD",
  },
  shareValue: {
    fontFamily: "Urbanist600",
    color: "#0B1F33",
  },
  shareHint: {
    color: "#D1E6FF",
    fontSize: 14,
  },
  errorBanner: {
    backgroundColor: "#FEF2F2",
    borderColor: "#FECACA",
    borderWidth: 1,
    borderRadius: 12,
    padding: 12,
  },
  errorBannerText: {
    color: "#981B1B",
  },
  loader: {
    paddingVertical: 12,
  },
  emptyState: {
    borderRadius: 16,
    borderWidth: 1,
    borderColor: "#E2E4EA",
    padding: 24,
    gap: 8,
    backgroundColor: "#FFFFFF",
  },
  emptyTitle: {
    fontFamily: "Urbanist600",
    color: "#1F242F",
  },
  emptyCopy: {
    color: "#4D5360",
  },
  keysList: {
    gap: 16,
  },
  keyCard: {
    borderRadius: 16,
    borderWidth: 1,
    borderColor: "#E2E4EA",
    padding: 20,
    gap: 12,
    backgroundColor: "#FFFFFF",
  },
  keyHeader: {
    flexDirection: "row",
    justifyContent: "space-between",
    alignItems: "flex-start",
    gap: 12,
  },
  keyTitle: {
    fontFamily: "Urbanist600",
    color: "#1F242F",
    flex: 1,
  },
  keyPrefix: {
    color: "#4D5360",
    fontSize: 14,
  },
  metaRow: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 20,
  },
  metaItem: {
    gap: 2,
  },
  metaLabel: {
    color: "#6B7280",
    fontSize: 13,
  },
  metaValue: {
    color: "#1F242F",
    fontFamily: "Urbanist500",
  },
  badgeRow: {
    flexDirection: "row",
    flexWrap: "wrap",
    gap: 8,
  },
  scopeBadge: {
    paddingVertical: 4,
    paddingHorizontal: 10,
    borderRadius: 999,
    backgroundColor: "#E6F7FF",
  },
  scopeBadgeText: {
    color: "#025B7E",
    fontSize: 14,
  },
  centeredContainer: {
    flex: 1,
    justifyContent: "center",
    alignItems: "center",
    padding: 32,
  },
});

export default KeyManagementPage;
